import axios from "axios";
import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import TopBar from "./Topbar";
import citiesData from "../cities.json";
import useConfig from "../hooks/useConfig";
import statesData from "../states.json";
import Footer from "./Footer";
import { notifyUser } from "../utils/notifyUser";

// Utility to fetch on hold note (can be moved to a separate file if needed)
async function fetchOnHoldNote(email) {
    try {
        const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/auth/investor-on-hold-note`,
            { params: { email } },
        );
        return res.data;
    } catch{
        return {};
    }
}

function AQIAccount({ user, setUser }) {
    // Redirect to account creation if application_submitted is 0

    // Sidebar open/close state (removed local sidebar)
    // const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    // Fetch and set on hold note for investors

    // Prefill first/last name from localStorage full name (from InvestorConsent)

    const [activeSection] = useState("generalInfo");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [birthdate, setBirthDate] = useState("");
    const [address1, setAddress1] = useState("");
    const [address2, setAddress2] = useState("");
    const [city, setCity] = useState("");
    const [state, setState] = useState("");
    const [zipCode, setZipCode] = useState("");
    const [, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [cities, setCities] = useState([]);
    const [linkedinUrl, setLinkedinUrl] = useState("");

    const [formDisabled, setFormDisabled] = useState(false);
    const [rejectionInfo, setRejectionInfo] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [status, setStatus] = useState("");
    const [reEdit, setReEdit] = useState(0);
    const [, setViewMode] = useState("form");
    const [localUser] = useState(null);
    const [ssn, setSsn] = useState("");
    const [, setShowConsentModal] = useState(true);
    const [, setOtpVerified] = useState(false);
    // On hold feedback state
    const [onHoldNote, setOnHoldNote] = useState("");
    const [onHold, setOnHold] = useState(false);

    // Accredited investor state variables
    const [isAccreditedInvestor, setIsAccreditedInvestor] = useState("");
    const [accreditedInvestorType, setAccreditedInvestorType] = useState("");
    const [netWorth, setNetWorth] = useState("");
    const [yearlyIncome, setYearlyIncome] = useState("");
    const { getConfig, loading: configLoading } = useConfig();
    const phoneVerificationEnabled =
        getConfig("ENABLE_PHONE_VERIFICATION") === "true";

    const effectiveUser = user || localUser;
    const navigate = useNavigate();
    const titleRef = useRef(null);
    const ssnRegex = /^\d{3}-\d{2}-\d{4}$/;

    // Function to check if phone number is already verified
    // Create a helper function for handling disabled phone verification
    // const handleDisabledVerification = () => {
    //     setOtpVerified(true);
    //     toast.success(
    //         "Phone verification is disabled. Your phone number has been accepted.",
    //     );
    // };

    // Function to check if phone number is already verified
    const checkPhoneVerification = async (phoneNumber) => {
        // Skip verification if phone number is empty, verification is disabled, or config is still loading
        if(!phoneNumber || configLoading) return;

        // If phone verification is disabled, automatically set as verified
        if(!phoneVerificationEnabled) {
            setOtpVerified(true);
            return;
        }

        try {
            // Pass the current user's email to check if phone belongs to another user
            const res = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/check-verification/${phoneNumber}`,
                {
                    params: { email: email },
                },
            );

            if(res.data.verified) {
                setOtpVerified(true);
            } else if(res.data.alreadyInUse) {
                notifyUser(null, "error", "Phone number is already in use by another account. Please use a different number.", "toast", null, null, null, null, null, false, false, "top-center");
            }
        } catch(err) {
            if(err.response?.data?.alreadyInUse) {
                notifyUser(null, "error", "Phone number is already in use by another account. Please use a different number.", "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                console.error(
                    "Failed to check phone verification status:",
                    err,
                );
            }
        }
    };
    useEffect(() => {
        const fetchAndSetOnHoldNote = async () => {
            if(!email) return;
            const data = await fetchOnHoldNote(email);
            if(data && data.on_hold_note) {
                setOnHoldNote(data.on_hold_note);
                setOnHold(true);
            } else {
                setOnHoldNote("");
                setOnHold(false);
            }
        };
        fetchAndSetOnHoldNote();
    }, [email]);
    useEffect(() => {
        const currentUser =
            user || JSON.parse(localStorage.getItem("user") || "{}") || {};
        // Only run on dashboard route
        if(window.location.pathname.includes("dashboard")) {
            if(
                currentUser.role === "investor" &&
                String(currentUser.application_submitted) === "0"
            ) {
                navigate("/account-creation");
            }
        }
    }, [user, navigate]);
    useEffect(() => {
        // Only prefill if both fields are empty (not loaded from backend)
        if(!firstName && !lastName) {
            const fullName = localStorage.getItem("investorSignatureName");
            if(fullName && fullName.trim()) {
                const parts = fullName.trim().split(" ");
                if(parts.length === 1) {
                    setFirstName(parts[0]);
                } else if(parts.length > 1) {
                    setFirstName(parts[0]);
                    setLastName(parts.slice(1).join(" "));
                }
            }
        }
    }, []);
    // Fetch form prefill data from backend
    useEffect(() => {
        const email = effectiveUser?.email || localStorage.getItem("userEmail");
        if(!email) return;

        const fetchPrefillData = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/auth/investor-info`,
                    {
                        params: { email },
                    },
                );

                const data = response.data;

                if(data.firstName && data.firstName.trim()) {
                    setFirstName(data.firstName);
                } else if(
                    (!data.firstName || !data.firstName.trim()) &&
                    data.signature_text &&
                    data.signature_text.trim()
                ) {
                    // Prefill from signature_text if firstName is empty
                    const parts = data.signature_text.trim().split(" ");
                    if(parts.length === 1) {
                        setFirstName(parts[0]);
                    } else if(parts.length > 1) {
                        setFirstName(parts[0]);
                    }
                }
                if(data.lastName && data.lastName.trim()) {
                    setLastName(data.lastName);
                } else if(
                    (!data.lastName || !data.lastName.trim()) &&
                    data.signature_text &&
                    data.signature_text.trim()
                ) {
                    // Prefill from signature_text if lastName is empty
                    const parts = data.signature_text.trim().split(" ");
                    if(parts.length > 1) {
                        setLastName(parts.slice(1).join(" "));
                    }
                }
                setEmail(data.email || "");
                setPhone(data.phone_number || "");

                // Check if phone number is already verified when form loads
                if(data.phone_number) {
                    checkPhoneVerification(data.phone_number);
                }

                setAddress1(data.address1 || "");
                setAddress2(data.address2 || "");
                setCity(data.city || "");
                setState(data.state || "");
                setZipCode(data.zipcode || "");
                setLinkedinUrl(data.linkedin || "");
                setBirthDate(
                    data.birthdate ? data.birthdate.split("T")[0] : "",
                );
                setStatus(data.status || "");
                setReEdit(data.re_edit || 0);
                setSsn(data.ssn_masked || "");
                setIsAccreditedInvestor(
                    data.is_accredited_investor === "Yes"
                        ? "true"
                        : data.is_accredited_investor === "No"
                          ? "false"
                          : data.is_accredited_investor || "",
                );
                setAccreditedInvestorType(data.accredited_investor_type || "");
                setNetWorth(data.net_worth || "");
                setYearlyIncome(data.yearly_income || "");

                if(data.re_edit === 1) {
                    setFormDisabled(false);
                    setIsEditing(true);
                }
                if(data.status === "rejected") {
                    setRejectionInfo({
                        status: data.status,
                        rejection_note: data.rejection_note || "",
                    });
                    setFormDisabled(true);
                    setIsSubmitted(true);
                } else if(
                    (data.status === "pending" || data.status === "approved") &&
                    data.submitted
                ) {
                    setIsSubmitted(true);
                    setFormDisabled(true);
                    // Only hide modal if actually submitted
                    setShowConsentModal(false);
                } else {
                    // Show modal if not submitted
                    setShowConsentModal(true);
                }
                if(data.state) {
                    setCities(citiesData[data.state] || []);
                }
            } catch(error) {
                console.error("Error fetching prefill data:", error);
            }
        };
        fetchPrefillData();
    }, [effectiveUser, configLoading]);

    useEffect(() => {
        if(titleRef.current) {
            titleRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, []);

    const validateForm = () => {
        // Validate LinkedIn URL
        const linkedinRegex = /^https:\/\/(www\.)?linkedin\.com\/.*$/;
        if(linkedinRegex.test(linkedinUrl)) {
            notifyUser(null, "error", "Please enter a valid LinkedIn URL.", "toast", null, null, null, null, null, false, false, "top-center");
            return false;
        }

        // Validate required fields
        if(
            !firstName ||
            !lastName ||
            !email ||
            !address1 ||
            !city ||
            !state ||
            !zipCode ||
            !birthdate ||
            !ssn
        ) {
            notifyUser(null, "error", "Please fill out all fields in the General Info section.", "toast", null, null, null, null, null, false, false, "top-center");
            return false;
        }

        // Validate ZIP code format
        if(!/^\d{5}$/.test(zipCode)) {
            notifyUser(null, "error", "Please enter a valid 5-digit ZIP code.", "toast", null, null, null, null, null, false, false, "top-center");
            return false;
        }

        // Validate SSN format
        if(!ssnRegex.test(ssn)) {
            notifyUser(null, "error", "SSN must be in the format ###-##-####", "toast", null, null, null, null, null, false, false, "top-center");
            return false;
        }

        return true;
    };

    useEffect(() => {
        if(state) {
            // Set cities based on the selected state
            setCities(citiesData[state] || []);
            // Reset city to empty when state changes
            setCity("");
        }
    }, [state]);

    useEffect(() => {
        const path = window.location.pathname;
        if(path.includes("dashboard")) {
            // show sidebar, no topbar/footer
            setViewMode("dashboard");
        } else {
            // show topbar/footer
            setViewMode("form");
        }
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if(!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        const localUser = JSON.parse(localStorage.getItem("user") || "{}");

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/account-creation`,
                {
                    firstName,
                    lastName,
                    email,
                    // companyName,
                    // dba,
                    address1,
                    address2,
                    city,
                    state,
                    birthdate,
                    zipCode,
                    linkedin: linkedinUrl,
                    userId: user?.id || localUser?.id,
                    ssn,
                    isAccreditedInvestor,
                    accreditedInvestorType: accreditedInvestorType || null,
                    netWorth: netWorth || null,
                    yearlyIncome: yearlyIncome || null,
                },
            );

            if(response.status === 200) {
                notifyUser(null, "success", "Information saved successfully. Proceeding to phone verification...", "toast", null, null, null, null, null, false, false, "top-center");
                setIsSubmitted(true);
                setFormDisabled(true);
                setShowConsentModal(false);

                // Update localStorage user data
                const currentUser = JSON.parse(
                    localStorage.getItem("user") || "{}",
                );
                const updatedUser = {
                    ...currentUser,
                    role: "investor",
                    submitted: true,
                    application_submitted: 1,
                    isProfileVerified: "true",
                };
                localStorage.setItem("user", JSON.stringify(updatedUser));

                // Update parent user state if setUser is available
                if(setUser) {
                    setUser((prev) => ({
                        ...prev,
                        role: "investor",
                        submitted: true,
                        application_submitted: 1,
                        isProfileVerified: "true",
                    }));
                }

                // Dispatch custom event to trigger user refresh in App component
                const userUpdatedEvent = new CustomEvent("user-logged-in", {
                    detail: updatedUser,
                });
                window.dispatchEvent(userUpdatedEvent);

                // Navigate to next step (phone verification) after successful account creation
                setTimeout(() => navigate("/phone-verification"), 2000);
            } else {
                notifyUser(null, "error", "Failed to continue.", "toast", null, null, null, null, null, false, false, "top-center");
            }
        } catch(error) {
            console.error("Form submission error:", error);
            if(error.response) {
                // Server responded with an error status
                const errorMessage =
                    error.response.data?.message ||
                    error.response.data?.error ||
                    "Failed to submit form.";
                notifyUser(null, "error", errorMessage, "toast", null, null, null, null, null, false, false, "top-center");
            } else if(error.request) {
                // Request was made but no response received
                notifyUser(null, "error", "Network error. Please check your connection.", "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                // Something else happened
                notifyUser(null, "error", "An unexpected error occurred.", "toast", null, null, null, null, null, false, false, "top-center");
            }
        } finally {
            setIsSubmitting(false);
        }
    };
    // const handleSendOtp = async () => {
    //     if(!phoneRegex.test(phone)) {
    //         toast.error("Please enter a valid phone number.");
    //         return;
    //     }

    //     // If phone verification is disabled, automatically set as verified
    //     if(!phoneVerificationEnabled) {
    //         handleDisabledVerification();
    //         return;
    //     }

    //     setOtpLoading(true);
    //     try {
    //         const res = await axios.post(
    //             `${process.env.REACT_APP_BACKEND_URL}/api/send-otp`,
    //             {
    //                 phoneNumber: phone,
    //                 email: email,
    //             }
    //         );

    //         // Check if verification is disabled or automatically verified
    //         if(res.data.disabled || res.data.verified) {
    //             setOtpVerified(true);
    //             toast.success(
    //                 "Phone verification is disabled. Your phone number has been accepted."
    //             );
    //         } else {
    //             setOtpSent(true);
    //             toast.success("OTP sent to your phone.");
    //         }
    //     } catch(err) {
    //         console.error("Send OTP failed:", err);
    //         if(err.response?.data?.alreadyInUse) {
    //             toast.error(
    //                 "Phone number is already in use by another account. Please use a different number."
    //             );
    //         } else {
    //             toast.error("Failed to send OTP.");
    //         }
    //     } finally {
    //         setOtpLoading(false);
    //     }
    // };
    // const handleVerifyOtp = async () => {
    //     if(!otp) {
    //         toast.error("Enter the OTP received on your phone.");
    //         return;
    //     }

    //     // If phone verification is disabled, automatically set as verified
    //     if(!phoneVerificationEnabled) {
    //         handleDisabledVerification();
    //         return;
    //     }

    //     setOtpLoading(true);
    //     try {
    //         const res = await axios.post(
    //             `${process.env.REACT_APP_BACKEND_URL}/api/verify-otp`,
    //             {
    //                 phoneNumber: phone,
    //                 otp,
    //                 email: email,
    //             }
    //         );

    //         // Check if verification is disabled
    //         if(res.data.disabled) {
    //             setOtpVerified(true);
    //             toast.success(
    //                 "Phone verification is disabled. Your phone number has been accepted."
    //             );
    //         } else {
    //             setOtpSent(true);
    //             setOtpVerified(true);
    //             toast.success("Phone number verified!");
    //         }
    //     } catch(err) {
    //         console.error("OTP verification failed:", err);
    //         toast.error("Invalid or expired OTP.");
    //     } finally {
    //         setOtpLoading(false);
    //     }
    // };
    // Check phone verification on component mount or when phone number changes
    useEffect(() => {
        if(phone && !configLoading) {
            // Skip verification if disabled, otherwise check if already verified
            if(!phoneVerificationEnabled) {
                setOtpVerified(true);
            } else {
                checkPhoneVerification(phone);
            }
        }
    }, [phone, phoneVerificationEnabled, configLoading]);

    // Dynamically update the page title
    useEffect(() => {
        document.title = "investorAccountCreation | AQI";
    }, []);

    // Handle phone number input changes
    // const handlePhoneChange = (newPhone) => {
    //     // Reset verification status if phone number changes while verification is enabled
    //     if(phone !== newPhone && !configLoading && phoneVerificationEnabled) {
    //         setOtpVerified(false);
    //         setOtpSent(false);
    //         setOtp("");
    //     }
    //     setPhone(newPhone);
    // };

    // Helper to reset phone verification state
    // const resetPhoneVerification = () => {
    //     setOtpVerified(false);
    //     setOtpSent(false);
    //     setOtp("");
    // };

    return (
        <>
            {!isSubmitted && <TopBar />}

            {/* Progress Bar */}
            {!isSubmitted && (
                <div className="bg-gray-100 p-4">
                    <div className="max-w-6xl mx-auto">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm text-gray-600">
                                Step 3 of 5
                            </span>
                            <span className="text-sm text-gray-600">60%</span>
                        </div>
                        <div className="w-full bg-gray-300 rounded-full h-2">
                            <div
                                className="bg-navy h-2 rounded-full transition-all duration-300"
                                style={{ width: "60%" }}
                            ></div>
                        </div>
                    </div>
                </div>
            )}
            {/* Always show the on-hold note bar if present */}
            {onHold && onHoldNote && (
                <div className="bg-yellow-100 text-[#840C28] p-3 rounded-xl mb-4 text-sm font-medium italic text-center font-poppins mx-2">
                    <strong>Account On Hold:</strong> {onHoldNote}
                </div>
            )}
            {/* Status and info banners */}
            {isSubmitted && status === "pending" && reEdit === 1 && (
                <div className="p-3 mb-4 text-sm font-semibold text-center text-green-800 bg-green-100 rounded-xl font-poppins mx-2">
                    Your edit request has been approved.{" "}
                    <span
                        className="underline text-navy cursor-pointer"
                        onClick={() => navigate("/investor-dashboard")}
                    >
                        Click here to update your investor account.
                    </span>
                </div>
            )}
            {rejectionInfo?.status !== "rejected" &&
                status === "pending" &&
                isSubmitted &&
                reEdit !== 1 && (
                    <div className="bg-yellow-100 text-[#840C28] p-2 rounded-xl mb-4 text-sm font-regular italic text-center font-poppins mx-2">
                        Your application has been submitted and is under review.
                        You can view it, but edits are disabled.
                    </div>
                )}
            {rejectionInfo?.status === "rejected" &&
                rejectionInfo?.rejection_note && (
                    <div className="bg-red-100 text-[#840C28] p-3 rounded-xl mb-4 text-sm font-medium italic text-center font-poppins whitespace-pre-line mx-2">
                        <strong>Rejection Note:</strong>{" "}
                        {rejectionInfo.rejection_note}
                    </div>
                )}
            {rejectionInfo?.status !== "rejected" && !isSubmitted && (
                <div className="flex justify-end mb-4 mx-2"></div>
            )}
            {/* Always show the form, regardless of formDisabled */}
            <div className="flex flex-col min-h-screen bg-white">
                <main className="flex justify-center flex-grow px-2 sm:px-4">
                    <div className="w-full max-w-7xl px-2 sm:px-4 md:px-6 lg:px-8 pt-2 pb-4 bg-white shadow-xl rounded-xl sm:rounded-xl-xl">
                        {/* Navigation Buttons */}
                        {activeSection === "generalInfo" && (
                            <div className="flex flex-col items-center mb-2 mt-0.5 px-2">
                                <h1 className="text-center text-2xl sm:text-3xl md:text-4xl text-navy font-semibold font-poppins">
                                    Create Your AQi Investor Account
                                </h1>

                                <p className="text-center text-base sm:text-lg text-navy mt-2 font-semibold font-poppins">
                                    Help Us Verify Your Identity
                                </p>
                                <p className="text-center text-sm sm:text-base md:text-lg lg:text-xl text-navy mt-2 font-semibold font-poppins">
                                    Before you can invest, we&apos;re required
                                    by law to collect few key details. This step
                                    helps ensure compliance with SEC regulations
                                    and keeps the AQi platform secure for all
                                    users.
                                </p>
                            </div>
                        )}
                        {/* Form Sections */}
                        {activeSection === "generalInfo" && (
                            <form
                                onSubmit={handleSubmit}
                                className="mt-4 space-y-4 px-2"
                            >
                                {/* First Name and Last Name */}
                                <div className="flex flex-col md:flex-row gap-4">
                                    {/* First Name */}
                                    <div className="flex flex-col w-full md:w-1/2">
                                        <label className="text-navy font-semibold font-poppins">
                                            First Name: *
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="First Name"
                                            value={firstName}
                                            onChange={(e) =>
                                                setFirstName(e.target.value)
                                            }
                                            disabled={formDisabled}
                                            className="w-full p-2 border border-navy rounded-xl font-poppins"
                                            required
                                        />
                                    </div>

                                    {/* Last Name */}
                                    <div className="flex flex-col w-full md:w-1/2">
                                        <label className="text-navy font-semibold font-poppins">
                                            Last Name: *
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Last Name"
                                            value={lastName}
                                            onChange={(e) =>
                                                setLastName(e.target.value)
                                            }
                                            disabled={formDisabled}
                                            className="w-full p-2 border border-navy rounded-xl font-poppins"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="flex flex-col lg:flex-row gap-4">
                                    {/* Email Address and SSN Column */}
                                    <div className="flex flex-col w-full lg:w-1/2 space-y-3">
                                        <div>
                                            <label className="text-navy font-semibold font-poppins">
                                                SSN: *
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="123-45-6789"
                                                value={ssn}
                                                onChange={(e) => {
                                                    const digits =
                                                        e.target.value.replace(
                                                            /\D/g,
                                                            "",
                                                        );
                                                    setSsn(digits);
                                                }}
                                                disabled={
                                                    formDisabled && !isEditing
                                                }
                                                className="w-full p-2 border border-navy rounded-xl font-poppins"
                                                required
                                            />
                                            {ssn?.startsWith("**") &&
                                                !isEditing && (
                                                    <div className="mt-1 text-xs italic text-gray-500">
                                                        Enter full SSN to update
                                                    </div>
                                                )}
                                        </div>
                                        <div className="mt-2">
                                            <label className="text-navy font-semibold font-poppins">
                                                Email Address:*
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="Email Address"
                                                value={email}
                                                onChange={(e) =>
                                                    setEmail(e.target.value)
                                                }
                                                disabled={true}
                                                className="w-full p-2 border border-navy rounded-xl font-poppins"
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* LinkedIn and Birthday Row */}
                                <div className="flex flex-col md:flex-row gap-4">
                                    <div className="flex flex-col w-full md:w-1/2">
                                        <label className="text-navy font-semibold font-poppins">
                                            LinkedIn URL:
                                        </label>
                                        <input
                                            type="url"
                                            placeholder="https://www.linkedin.com/in/yourname"
                                            value={linkedinUrl}
                                            onChange={(e) =>
                                                setLinkedinUrl(e.target.value)
                                            }
                                            disabled={formDisabled}
                                            className="w-full p-2 border border-navy rounded-xl font-poppins"
                                        />
                                    </div>

                                    <div className="flex flex-col w-full md:w-1/2">
                                        <label className="text-navy font-semibold font-poppins">
                                            Birth Date: *
                                        </label>
                                        <input
                                            type="date"
                                            name="birthdate"
                                            value={birthdate}
                                            onChange={(e) =>
                                                setBirthDate(e.target.value)
                                            }
                                            disabled={formDisabled}
                                            className="w-full p-2 border border-navy rounded-xl font-poppins"
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Address Section */}
                                <div className="flex flex-col lg:flex-row gap-4">
                                    {/* Address Line 1 */}
                                    <div className="flex flex-col w-full lg:w-1/2">
                                        <label className="text-navy font-semibold font-poppins">
                                            Address: *
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Address"
                                            value={address1}
                                            onChange={(e) =>
                                                setAddress1(e.target.value)
                                            }
                                            disabled={formDisabled}
                                            className="w-full p-2 border border-navy rounded-xl font-poppins"
                                            required
                                        />
                                    </div>

                                    {/* State / City / Zip Container */}
                                    <div className="flex flex-col w-full lg:w-1/2">
                                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                                            <div className="flex flex-col w-full sm:w-1/3">
                                                <label className="text-navy font-semibold font-poppins">
                                                    State: *
                                                </label>
                                                <select
                                                    value={state}
                                                    onChange={(e) =>
                                                        setState(e.target.value)
                                                    }
                                                    disabled={formDisabled}
                                                    className="w-full p-2 border border-navy rounded-xl font-poppins"
                                                    required
                                                >
                                                    <option value="">
                                                        Select State
                                                    </option>
                                                    {Object.keys(
                                                        statesData,
                                                    ).map((stateCode) => (
                                                        <option
                                                            key={stateCode}
                                                            value={stateCode}
                                                        >
                                                            {
                                                                statesData[
                                                                    stateCode
                                                                ]
                                                            }
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="flex flex-col w-full sm:w-1/3">
                                                <label className="text-navy font-semibold font-poppins">
                                                    City: *
                                                </label>
                                                {formDisabled && !isEditing ? (
                                                    <div className="w-full p-2 border border-navy rounded-xl font-poppins bg-gray-100 text-gray-700 min-h-[42px] flex items-center">
                                                        {city || (
                                                            <span className="italic text-gray-400">
                                                                No city selected
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <select
                                                        value={city}
                                                        onChange={(e) =>
                                                            setCity(
                                                                e.target.value,
                                                            )
                                                        }
                                                        disabled={
                                                            formDisabled &&
                                                            !isEditing
                                                        }
                                                        className="w-full p-2 border border-navy rounded-xl font-poppins"
                                                        required
                                                    >
                                                        <option value="">
                                                            Select City
                                                        </option>
                                                        {cities.map(
                                                            (
                                                                cityName,
                                                                index,
                                                            ) => (
                                                                <option
                                                                    key={index}
                                                                    value={
                                                                        cityName
                                                                    }
                                                                >
                                                                    {cityName}
                                                                </option>
                                                            ),
                                                        )}
                                                    </select>
                                                )}
                                            </div>

                                            <div className="flex flex-col w-full sm:w-1/3">
                                                <label className="text-navy font-semibold font-poppins">
                                                    Zip Code: *
                                                </label>
                                                <input
                                                    type="text"
                                                    placeholder="Zip Code"
                                                    value={zipCode}
                                                    onChange={(e) =>
                                                        setZipCode(
                                                            e.target.value,
                                                        )
                                                    }
                                                    disabled={formDisabled}
                                                    className="w-full p-2 border border-navy rounded-xl font-poppins"
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="flex justify-center w-full">
                                    <button
                                        type="submit"
                                        disabled={formDisabled || isSubmitted}
                                        className="w-full max-w-xs sm:w-60 bg-navy text-white py-3 rounded-xl shadow-md hover:bg-navy-2 font-bold font-poppins"
                                    >
                                        {formDisabled ? "SUBMITTED" : "NEXT"}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </main>

                {!isSubmitted && <Footer />}
            </div>
        </>
    );
}

AQIAccount.propTypes = {
    user: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    setUser: PropTypes.func.isRequired,
};

export default AQIAccount;
